// Slice H2 — Curated Strategy Template Gallery.
//
// Modal surface that surfaces the 4 curated starter templates shipped
// with the app. Distinct from the existing `TemplateBrowser.tsx` —
// that one is the Supabase-backed user-generated marketplace.
// Co-exist: this is the curated, no-network, instant-load surface.
//
// Entry points (per H2 design):
//   1. Empty Builder state ("Start from a template" CTA)
//   2. Builder toolbar button ("Templates" with sparkle icon)
//   3. AI Optimize seed selector (when refining with no seed)
//   4. (Deferred to H2.1) H1 "Try a different family" cross-link
//
// Loading flow:
//   • Card click → calls onLoad with the CuratedTemplate
//   • Parent caller wraps via useReplaceStrategyGuard so unsaved work
//     triggers the confirmation dialog
//   • Telemetry: opened on mount, loaded on click, load_cancelled
//     when the Replace guard's modal is dismissed
//
// Scope guardrails:
//   • NO Supabase calls (curated templates are static imports)
//   • NO marketplace features (favoriting, ratings, sharing)
//   • NO auto-backtest (per design Q4)
//   • Caveat copy front-and-center

import React, { useState, useEffect, useMemo } from 'react';
import { X, BookOpen, Shield, TrendingUp, RefreshCw, ArrowUpRight, AlertTriangle } from 'lucide-react';
import { track } from '../../utils/analytics';
import {
  CURATED_TEMPLATES,
  type CuratedTemplate,
  type TemplateFamily,
} from '../../templates';

interface Props {
  /** Whether the modal is currently visible. Parent controls open/close. */
  isOpen: boolean;
  /** Where the modal was opened from. Recorded in telemetry so we can
   *  measure which entry point drives template loads. */
  entryPoint: 'empty-builder' | 'toolbar' | 'ai-optimize-seed' | 'h1-suggestion';
  /** Called when the user clicks Load on a template card. Parent
   *  should route through `useReplaceStrategyGuard.requestReplace`. */
  onLoad: (template: CuratedTemplate) => void;
  /** Called when the user closes the modal without loading. */
  onClose: () => void;
}

const FAMILY_LABEL: Record<TemplateFamily | 'all', string> = {
  all: 'All',
  trend: 'Trend',
  'mean-reversion': 'Mean-reversion',
  breakout: 'Breakout',
  'prop-firm': 'Prop-firm',
};

const FAMILY_ICON: Record<TemplateFamily, React.ReactNode> = {
  trend: <TrendingUp className="w-4 h-4" />,
  'mean-reversion': <RefreshCw className="w-4 h-4" />,
  breakout: <ArrowUpRight className="w-4 h-4" />,
  'prop-firm': <Shield className="w-4 h-4" />,
};

const FAMILY_FILTERS: ReadonlyArray<TemplateFamily | 'all'> = [
  'all', 'trend', 'mean-reversion', 'breakout', 'prop-firm',
];

const CuratedTemplateGallery: React.FC<Props> = ({ isOpen, entryPoint, onLoad, onClose }) => {
  const [activeFamily, setActiveFamily] = useState<TemplateFamily | 'all'>('all');

  // Telemetry: opened. Fires once per modal-open, NOT on every re-render.
  useEffect(() => {
    if (isOpen) {
      track('template_gallery_opened', { entry_point: entryPoint });
    }
  }, [isOpen, entryPoint]);

  // Close on ESC + outside click. Standard modal a11y.
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  const visibleTemplates = useMemo(() => {
    if (activeFamily === 'all') return CURATED_TEMPLATES;
    return CURATED_TEMPLATES.filter(t => t.family === activeFamily);
  }, [activeFamily]);

  const handleLoad = (template: CuratedTemplate) => {
    track('template_loaded', { slug: template.slug, version: template.version, entry_point: entryPoint });
    onLoad(template);
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="curated-template-gallery-title"
      data-testid="curated-template-gallery"
      data-entry-point={entryPoint}
    >
      <div
        className="bg-white rounded-lg shadow-2xl max-w-5xl w-full max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-fuchsia-50 rounded-lg">
              <BookOpen className="w-5 h-5 text-fuchsia-700" />
            </div>
            <div>
              <h2
                id="curated-template-gallery-title"
                className="text-lg font-semibold text-gray-900"
              >
                Strategy templates
              </h2>
              <p className="text-xs text-gray-600 mt-0.5 leading-snug max-w-2xl">
                Starting points for your strategy. <strong>Templates are NOT
                guaranteed profitable systems</strong> — they&apos;re well-formed
                graphs you can backtest, validate, and modify. Every template
                still goes through walk-forward validation on YOUR asset
                before deploying.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 hover:bg-gray-100 rounded text-gray-500"
            aria-label="Close gallery"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Family filter pills */}
        <div className="px-6 py-3 border-b border-gray-100 flex items-center gap-2 flex-wrap">
          <span className="text-xs text-gray-500 mr-1">Filter:</span>
          {FAMILY_FILTERS.map((f) => (
            <button
              key={f}
              type="button"
              onClick={() => setActiveFamily(f)}
              data-testid={`family-filter-${f}`}
              data-active={activeFamily === f}
              className={
                'text-xs px-2.5 py-1 rounded-full border transition-colors ' +
                (activeFamily === f
                  ? 'bg-fuchsia-600 text-white border-fuchsia-600'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50')
              }
            >
              {FAMILY_LABEL[f]}
            </button>
          ))}
        </div>

        {/* Card grid */}
        <div className="px-6 py-4 overflow-y-auto flex-1">
          {visibleTemplates.length === 0 ? (
            <div className="text-sm text-gray-500 py-8 text-center">
              No templates match this filter. Try &ldquo;All&rdquo; or pick a different family.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {visibleTemplates.map((tpl) => (
                <TemplateCard key={tpl.slug} template={tpl} onLoad={handleLoad} />
              ))}
            </div>
          )}
        </div>

        {/* Footer caveat */}
        <div className="px-6 py-3 border-t border-gray-200 bg-amber-50 text-[11px] text-amber-900 leading-snug flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5 text-amber-700" />
          <span>
            Templates are starting points, not guaranteed profitable systems.
            Every template still needs to pass walk-forward validation on
            YOUR asset + timeframe before deploying.
          </span>
        </div>
      </div>
    </div>
  );
};

// ── Template card ────────────────────────────────────────────────────

const TemplateCard: React.FC<{
  template: CuratedTemplate;
  onLoad: (t: CuratedTemplate) => void;
}> = ({ template, onLoad }) => {
  return (
    <div
      className="border border-gray-200 rounded-lg p-4 hover:border-fuchsia-300 hover:shadow-sm transition-all bg-white"
      data-testid="curated-template-card"
      data-slug={template.slug}
      data-family={template.family}
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex items-center gap-2">
          <div className="p-1.5 bg-fuchsia-50 rounded text-fuchsia-700">
            {FAMILY_ICON[template.family]}
          </div>
          <div>
            <div className="text-sm font-semibold text-gray-900" data-testid="card-name">
              {template.name}
            </div>
            <div className="text-[10px] uppercase tracking-wide text-gray-500 mt-0.5">
              {FAMILY_LABEL[template.family]}
            </div>
          </div>
        </div>
        {template.propFirm && (
          <span
            className="text-[10px] px-1.5 py-0.5 bg-emerald-50 text-emerald-800 border border-emerald-200 rounded font-mono"
            title="Pre-attached prop-firm profile"
          >
            {template.propFirm.provider.toUpperCase()} · {template.propFirm.preset}
          </span>
        )}
      </div>

      <p className="text-xs text-gray-700 leading-relaxed mb-2" data-testid="card-best-for">
        {template.bestFor}
      </p>

      {/* Timeframe chips */}
      <div className="flex items-center gap-1 flex-wrap mb-2">
        <span className="text-[10px] text-gray-500">TF:</span>
        {template.timeframeGuidance.map((tf) => (
          <span
            key={tf}
            className="text-[10px] font-mono px-1.5 py-0.5 bg-gray-100 text-gray-700 rounded"
          >
            {tf}
          </span>
        ))}
      </div>

      {/* Asset guidance */}
      <div className="text-[10px] text-gray-600 mb-2 leading-snug">
        <span className="font-semibold">Assets: </span>{template.assetGuidance}
      </div>

      {/* Expected validation */}
      <div
        className="text-[10px] text-gray-700 bg-blue-50 border border-blue-200 rounded px-2 py-1.5 mb-2 leading-snug"
        data-testid="card-expected-validation"
      >
        <span className="font-semibold">Expected validation: </span>
        {template.expectedValidation}
      </div>

      {/* Risk note chips */}
      {template.riskNotes.length > 0 && (
        <div className="mb-3 space-y-0.5">
          {template.riskNotes.map((note, i) => (
            <div key={i} className="text-[10px] text-amber-800 leading-snug flex items-start gap-1">
              <span className="text-amber-600 mt-0.5">⚠</span>
              <span>{note}</span>
            </div>
          ))}
        </div>
      )}

      {/* CTA */}
      <button
        type="button"
        onClick={() => onLoad(template)}
        data-testid={`load-template-${template.slug}`}
        className="w-full mt-2 px-3 py-1.5 bg-fuchsia-600 hover:bg-fuchsia-700 text-white text-sm font-medium rounded transition-colors"
      >
        Load template
      </button>
    </div>
  );
};

export default CuratedTemplateGallery;
